import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { ODataModel, ODataObject } from '../../interfaces/odata-model.interface';
import { OdataResponse } from '../../interfaces/online-only.interface';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { ODataUtilities } from '../../utilities/odata-utilities';
import { HttpUtilitiesService } from '../http-utilities/http-utilities.service';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { OdataServiceInterface } from './odata.interface';

const KEYS_TO_STRIP_FROM_LOGGING = ['CSESignature', 'CustomerSignature'];
const stripSensitiveKeys = <T extends Record<string, unknown>>(obj: T): T =>
    Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k, KEYS_TO_STRIP_FROM_LOGGING.includes(k) ? '[stripped]' : v]),
    ) as T;

@Injectable({
    providedIn: 'root',
})
export class OdataService implements OdataServiceInterface {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly httpUtilities: HttpUtilitiesService,
        private readonly http: HttpClient,
        private readonly paceInsights: PaceInsightsService,
    ) {}

    public deepInsertRequest(data: ODataModel, endpoint: string, identifier: string): Observable<unknown> {
        return this.deepInsertOutModelRequest(data.getOutModel(), endpoint, identifier);
    }

    /**
     * Variant of `deepInsertRequest` for callers that already hold the serialized
     * out model — e.g. NgRx effects, whose action payloads cannot carry `*Model`
     * class instances.
     *
     * @param outModel the serialized out model to insert
     * @param endpoint the OData endpoint the out model belongs to
     * @param identifier idempotency discriminator for the deep-insert timestamp
     * @returns the transformed OData response
     */
    public deepInsertOutModelRequest(outModel: ODataObject, endpoint: string, identifier: string): Observable<unknown> {
        const computedEndpoint = ODataUtilities.removeLeadingSlash(endpoint);
        const payload = {
            Endpoint: computedEndpoint,
            TimeStamp: `${computedEndpoint}-${identifier}`,
            Json: EncodingUtilities.base64EncodeUnicode(JSON.stringify(outModel)),
        };
        const query = this.uriUtilities.getUriForContent() + ENDPOINTS.DeepInsertSet;
        const headers = this.httpUtilities.setHeadersForIdempotency(new HttpHeaders({}));
        this.paceInsights.trackEvent('DeepInsertRequest', {
            outModel: stripSensitiveKeys(outModel),
            endpoint,
            identifier,
        });

        return this.http.post<OdataResponse<unknown>>(query, payload, { headers }).pipe(
            map((response) => ODataUtilities.transformResponseDataToArray(response.d)),
            catchError((error) => {
                this.log.error(
                    'deepInsertRequest() - request error: ' +
                        JSON.stringify(error) +
                        ' - for Request: ' +
                        JSON.stringify(query),
                );
                return throwError(error);
            }),
        );
    }

    public postRequest(url: string, payload: ODataModel): Observable<unknown> {
        const headers = this.httpUtilities.setHeadersForIdempotency(new HttpHeaders({}));
        return this.http.post<OdataResponse<unknown>>(url, payload.getOutModel(), { headers }).pipe(
            map((response) => ODataUtilities.transformResponseDataToArray(response.d)),
            catchError((error) => {
                this.log.error(
                    '_postRequest - error (JSON): ',
                    JSON.stringify(error, Object.getOwnPropertyNames(error)),
                );
                return throwError(() => error);
            }),
        );
    }
}
